import * as React from "react";
import Card from "@mui/material/Card";
import Checkbox from "@mui/material/Checkbox";
import CardContent from "@mui/material/CardContent";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import { spotInstanceProductDescriptions } from "../../consts/spotInstanceProductDescriptions";
import FormControlLabel from "@mui/material/FormControlLabel";
import CardHeader from "@mui/material/CardHeader";
import CardActions from "@mui/material/CardActions";
import Button from "@mui/material/Button";
import { useCallback } from "react";
import { DebouncedTextField } from "../DebouncedTextField";
import InputAdornment from "@mui/material/InputAdornment";
import { IconButton, SxProps } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Close";
import { LabelInfoLink } from "../LabelInfoLink";

export interface FilterObject {
  InstanceType: string;
  ProductDescription: string;
  SupportedArchitecture: "arm64" | "i386" | "x86_64";
  HibernateCapableRequired: boolean;
}

interface FilterParams {
  filter: FilterObject;
  onUpdate: (filterObject: FilterObject) => void;
  onReset: () => void;
  disabled: boolean;
  sx?: SxProps;
}

export const getFilterInitialState = (): FilterObject => ({
  InstanceType: "",
  ProductDescription: "Linux/UNIX",
  HibernateCapableRequired: false,
  SupportedArchitecture: "x86_64",
});

export const Filter = ({
  filter,
  onUpdate,
  onReset,
  disabled,
  sx,
}: FilterParams) => {
  const handleReset = useCallback(() => {
    onUpdate(getFilterInitialState());
    onReset();
  }, [onReset, onUpdate]);

  const handleFilterKeyUpdate = useCallback(
    (key: keyof FilterObject, value: any) => {
      const newFilter = {
        ...filter,
        [key]: value,
      };

      onUpdate(newFilter);
    },
    [filter, onUpdate],
  );

  return (
    <Card sx={{ minWidth: 275, ...sx }}>
      <CardHeader title="Filter" />
      <CardContent
        sx={{ display: "flex", flexDirection: "column", gap: "20px" }}
      >
        <FormControl fullWidth>
          <DebouncedTextField
            disabled={disabled}
            label="Instance Type"
            value={filter.InstanceType}
            onChange={(event) => {
              handleFilterKeyUpdate("InstanceType", event.target.value);
            }}
            InputProps={{
              endAdornment: filter.InstanceType.length > 0 && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    disabled={disabled}
                    onClick={() => handleFilterKeyUpdate("InstanceType", "")}
                  >
                    <DeleteIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </FormControl>
        <FormControl fullWidth disabled={disabled}>
          <InputLabel>Operating System</InputLabel>
          <Select
            label="Operating System"
            value={filter.ProductDescription}
            onChange={(event: SelectChangeEvent) =>
              handleFilterKeyUpdate("ProductDescription", event.target.value)
            }
          >
            {spotInstanceProductDescriptions.map((productDescription) => (
              <MenuItem key={productDescription} value={productDescription}>
                {productDescription}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth disabled={disabled}>
          <InputLabel id="demo-simple-select-label">Architecture</InputLabel>
          <Select
            label="Architecture"
            value={filter.SupportedArchitecture}
            onChange={(event: SelectChangeEvent) =>
              handleFilterKeyUpdate("SupportedArchitecture", event.target.value)
            }
          >
            {["arm64", "i386", "x86_64"].map((architecture) => (
              <MenuItem key={architecture} value={architecture}>
                {architecture}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth disabled={disabled}>
          <FormControlLabel
            control={<Checkbox checked={filter.HibernateCapableRequired} />}
            label={
              <>
                Hibernate Capable{" "}
                <LabelInfoLink
                  target="_blank"
                  href="https://docs.aws.amazon.com/AWSEC2/latest/UserGuide/hibernate-spot-instances.html#spot-instance-hibernation-prerequisites"
                >
                  info
                </LabelInfoLink>
              </>
            }
            onChange={(event, checked) =>
              handleFilterKeyUpdate("HibernateCapableRequired", checked)
            }
          />
        </FormControl>
      </CardContent>
      <CardActions sx={{ justifyContent: "flex-end" }}>
        <Button size="small" onClick={handleReset} disabled={disabled}>
          Reset
        </Button>
      </CardActions>
    </Card>
  );
};
